import React, { useEffect, useState } from 'react';
import { Modal } from 'antd';
import { WhatsNewEntry } from '@src/dtos/whats-new.dto';
import CloseIcon from '@svg/close-circle.svg?react';
import Arrow from '@svg/chevron-right-circle.svg?react';
import './whatsNewDetailModal.scss';

interface WhatsNewDetailModalProps {
  /** The What's New entry to display, or null to render nothing */
  entry: WhatsNewEntry | null;
  /** Called when the modal is closed by the user */
  onClose: () => void;
}

export const WhatsNewDetailModal: React.FC<WhatsNewDetailModalProps> = ({ entry, onClose }) => {
  const [currentSlide, setCurrentSlide] = useState(0);

  // Reset slide index whenever a different entry is selected
  useEffect(() => {
    setCurrentSlide(0);
  }, [entry]);

  if (!entry) return null;

  const prevSlide = () => setCurrentSlide(prev => prev - 1);
  const nextSlide = () => setCurrentSlide(prev => prev + 1);

  return (
    <Modal
      open
      onCancel={onClose}
      footer={null}
      width={628}
      closable={false}
      className="whats-new-detail-modal"
    >
      <div data-testid="whats-new-detail-modal">
      {/* Image area */}
      <div className="whats-new-detail-modal__image-area" data-testid="whats-new-image-area">
        <div
          className="whats-new-detail-modal__slides-track"
          style={{ transform: `translateX(-${currentSlide * 100}%)` }}
        >
          {entry.slides.map((s) => (
            <div key={s.ImageComponent.name} className="whats-new-detail-modal__slide-frame">
              <div className="whats-new-detail-modal__image-bg">
                <s.ImageComponent />
              </div>
            </div>
          ))}
        </div>
        <button
          type="button"
          className="whats-new-detail-modal__close-btn"
          onClick={onClose}
          aria-label="Close"
          data-testid="whats-new-close-btn"
        >
          <CloseIcon />
        </button>
      </div>

      {/* Text stack */}
      <div className="whats-new-detail-modal__content">
        <div className="whats-new-detail-modal__text-group">
          <p className="whats-new-detail-modal__version">
            Dynamic Defense version {entry.version}
          </p>

          <div className="whats-new-detail-modal__text-track-wrapper">
            <div
              className="whats-new-detail-modal__text-track"
              style={{ transform: `translateX(-${currentSlide * 100}%)` }}
            >
              {entry.slides.map((s, i) => (
                <div key={s.ImageComponent.name} className="whats-new-detail-modal__text-slide" aria-hidden={i !== currentSlide}>
                  <div className="whats-new-detail-modal__title-desc">
                    <div className="whats-new-detail-modal__headline-row">
                      <h2 className="whats-new-detail-modal__headline">{s.headline}</h2>
                    </div>
                    <div className="whats-new-detail-modal__description">{s.description}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="whats-new-detail-modal__nav">
            {entry.slides.length > 1 && <>
                <button
                    type="button"
                    className="whats-new-detail-modal__arrow-btn"
                    onClick={prevSlide}
                    disabled={currentSlide === 0}
                    aria-label="Previous slide"
                    data-testid="whats-new-prev-slide"
                >
                    <Arrow />
                </button>

                <div
                    className="whats-new-detail-modal__dots"
                    data-testid="whats-new-slide-dots"
                >
                    {entry.slides.map((slide, i) => (
                    <span
                        key={slide.ImageComponent.name}
                        className={`whats-new-detail-modal__dot${i === currentSlide ? ' whats-new-detail-modal__dot--active' : ''}`}
                    />
                    ))}
                </div>

                <button
                    type="button"
                    className="whats-new-detail-modal__arrow-btn"
                    onClick={nextSlide}
                    disabled={currentSlide === entry.slides.length - 1}
                    aria-label="Next slide"
                    data-testid="whats-new-next-slide"
                >
                    <Arrow className="whats-new-detail-modal__arrow-right" />
                </button>
            </>}
        </div>
      </div>
      </div>
    </Modal>
  );
};
